import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { appConfigService } from '../services/apiService';

const AppConfigContext = createContext();

export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used within AppConfigProvider');
  }
  return context;
};

export const AppConfigProvider = ({ children }) => {
  const [schoolName, setSchoolName] = useState(`St. Thomas School`); // Default fallback
  const [schoolAddress, setSchoolAddress] = useState('Khalilabad, Sant Kabir Nagar'); // Default fallback
  const [themeColor, setThemeColor] = useState('#115263'); // Default fallback
  const [feeManagementEnabled, setFeeManagementEnabled] = useState(false); // Default to false
  const [promotionEnabled, setPromotionEnabled] = useState(false); // Default to false
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const isLoadingRef = useRef(false); // Ref to prevent multiple simultaneous loads

  const parseThemeValue = useCallback((value) => {
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      if (parsed?.primary) {
        return parsed.primary;
      }
      if (typeof parsed === 'string') {
        return parsed;
      }
    } catch (error) {
      // Not JSON, fall back to raw value
    }
    return value;
  }, []);

  const loadConfig = useCallback(async (forceReload = false) => {
    // Prevent multiple simultaneous loads or reloads (unless forced)
    if ((hasLoaded && !forceReload) || isLoadingRef.current) {
      return;
    }
    
    isLoadingRef.current = true;
    try {
      setLoading(true);
      if (forceReload) {
        setHasLoaded(false);
      }

      // Fetch theme color (public) - this doesn't require auth
      let publicThemeColor = null;
      try {
        const themeResponse = await appConfigService.getPublicTheme();
        const themeValue = themeResponse?.data?.config_value;
        publicThemeColor = parseThemeValue(themeValue);
        if (publicThemeColor) {
          setThemeColor(publicThemeColor);
        }
      } catch (error) {
        console.warn('Unable to load public theme configuration:', error);
      }

      // Check if user is authenticated (has token) for private configs
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        setHasLoaded(true);
        return;
      }

      // Single API call to get all configurations at once
      let allConfigs = null;
      try {
        const configResponse = await appConfigService.getAll();
        
        // Handle different response formats
        if (configResponse?.success && configResponse?.data) {
          allConfigs = Array.isArray(configResponse.data) ? configResponse.data : [configResponse.data];
        } else if (Array.isArray(configResponse)) {
          allConfigs = configResponse;
        } else if (configResponse?.data && Array.isArray(configResponse.data)) {
          allConfigs = configResponse.data;
        }
      } catch (err) {
        // Silently fail if unauthorized (user not logged in)
        if (err?.response?.status === 401) {
          setLoading(false);
          setHasLoaded(true);
          return;
        }
        console.warn('Unable to load app configurations:', err);
      }

      // Extract values from the single response
      if (allConfigs && Array.isArray(allConfigs)) {
        const configMap = {};
        allConfigs.forEach((config) => {
          if (config.config_key && config.config_value !== undefined) {
            configMap[config.config_key] = config.config_value;
          }
        });

        // Set SCHOOL_NAME
        if (configMap.SCHOOL_NAME) {
          setSchoolName(configMap.SCHOOL_NAME);
        }

        // Set SCHOOL_ADDRESS
        if (configMap.SCHOOL_ADDRESS) {
          setSchoolAddress(configMap.SCHOOL_ADDRESS);
        }

        // Set THEME (override public theme if available)
        if (configMap.THEME) {
          const themeValue = parseThemeValue(configMap.THEME);
          if (themeValue) {
            setThemeColor(themeValue);
          }
        } else if (publicThemeColor) {
          // Fallback to public theme if private theme not available
          setThemeColor(publicThemeColor);
        }

        // Set FEE_MANAGEMENT - check if value is "true" (case-insensitive)
        if (configMap.FEE_MANAGEMENT) {
          const feeManagementValue = String(configMap.FEE_MANAGEMENT).toLowerCase().trim();
          setFeeManagementEnabled(feeManagementValue === 'true');
        }

        // Set PROMOTION - check if value is "true" (case-insensitive)
        if (configMap.PROMOTION) {
          const promotionValue = String(configMap.PROMOTION).toLowerCase().trim();
          setPromotionEnabled(promotionValue === 'true');
        }
      }
    } catch (error) {
      console.error('Error loading app config:', error);
      // Keep default values on error
    } finally {
      setLoading(false);
      setHasLoaded(true);
      isLoadingRef.current = false;
    }
  }, [hasLoaded, parseThemeValue]);

  useEffect(() => {
    // Only load configs once on mount
    if (!hasLoaded && !isLoadingRef.current) {
      loadConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  const refreshConfig = useCallback(() => {
    setHasLoaded(false);
    loadConfig(true);
  }, [loadConfig]);

  return (
    <AppConfigContext.Provider value={{ schoolName, schoolAddress, themeColor, feeManagementEnabled, promotionEnabled, loading, refreshConfig }}>
      {children}
    </AppConfigContext.Provider>
  );
};

